import React, { useState } from 'react';
import { Handshake, CheckCircle2, TrendingUp, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { submitDealership } from '../services/api';

const Dealership = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', companyName: '', gstNumber: '',
    address: '', city: '', state: '', pincode: '', experience: '', businessType: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitDealership({
        name: formData.name, email: formData.email, phone: formData.phone,
        company_name: formData.companyName, gst_number: formData.gstNumber,
        address: formData.address, city: formData.city, state: formData.state,
        pincode: formData.pincode, experience: formData.experience,
        business_type: formData.businessType, message: formData.message
      });
      toast.success('Your dealership application has been submitted! Our team will review and contact you soon.');
      setFormData({ name: '', email: '', phone: '', companyName: '', gstNumber: '', address: '', city: '', state: '', pincode: '', experience: '', businessType: '', message: '' });
    } catch (err) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Competitive Margins',
      description: 'Attractive profit margins and volume-based incentives'
    },
    {
      icon: Award,
      title: 'Quality Products',
      description: 'Access to our complete range of premium tooling solutions'
    },
    {
      icon: CheckCircle2,
      title: 'Marketing Support',
      description: 'Comprehensive marketing materials and promotional support'
    },
    {
      icon: Handshake,
      title: 'Technical Training',
      description: 'Regular training sessions and technical assistance'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fb] to-white">
      {/* Header */}
      <section className="bg-[#2E3A6F] text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-4">
              <Handshake size={32} />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold">Become a Dealer</h1>
            <p className="text-lg text-white/90">
              Join our growing network of authorized dealers and grow your business with Namoh Toolings
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-3xl font-bold text-gray-900">Why Partner With Us?</h2>
              <p className="text-lg text-gray-600">
                Exclusive benefits for our authorized dealers
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-0 bg-gradient-to-br from-[#f8f9fb] to-white hover:shadow-xl transition-all duration-300 group">
                  <CardContent className="p-6 text-center space-y-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#2E3A6F]/10 group-hover:bg-[#2E3A6F] transition-colors duration-300">
                      <benefit.icon className="text-[#2E3A6F] group-hover:text-white transition-colors" size={28} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{benefit.title}</h3>
                    <p className="text-sm text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 bg-white">
              <CardContent className="p-8 space-y-6">
                <div className="text-center space-y-3">
                  <h2 className="text-2xl font-bold text-gray-900">Dealership Requirements</h2>
                  <p className="text-gray-600">Please ensure you meet the following criteria:</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Valid GST Registration',
                    'Minimum 2 years in business',
                    'Established customer base',
                    'Adequate storage facility',
                    'Sales team in place',
                    'Good market reputation'
                  ].map((req, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckCircle2 className="text-[#2E3A6F] flex-shrink-0" size={20} />
                      <span className="text-gray-700">{req}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 bg-gradient-to-br from-[#f8f9fb] to-white">
              <CardHeader>
                <CardTitle className="text-2xl text-center">Dealership Application Form</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 pb-2 border-b">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="John Doe"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="+91 98765 43210"
                      />
                    </div>
                  </div>

                  {/* Business Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 pb-2 border-b">Business Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="companyName">Company Name *</Label>
                        <Input
                          id="companyName"
                          required
                          value={formData.companyName}
                          onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                          placeholder="Your Company Pvt Ltd"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="gstNumber">GST Number *</Label>
                        <Input
                          id="gstNumber"
                          required
                          value={formData.gstNumber}
                          onChange={(e) => setFormData({...formData, gstNumber: e.target.value})}
                          placeholder="22AAAAA0000A1Z5"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address">Business Address *</Label>
                      <Input
                        id="address"
                        required
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        placeholder="Street address"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          required
                          value={formData.city}
                          onChange={(e) => setFormData({...formData, city: e.target.value})}
                          placeholder="City"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">State *</Label>
                        <Input
                          id="state"
                          required
                          value={formData.state}
                          onChange={(e) => setFormData({...formData, state: e.target.value})}
                          placeholder="State"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pincode">PIN Code *</Label>
                        <Input
                          id="pincode"
                          required
                          value={formData.pincode}
                          onChange={(e) => setFormData({...formData, pincode: e.target.value})}
                          placeholder="141010"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 pb-2 border-b">Additional Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="experience">Years in Business *</Label>
                        <Input
                          id="experience"
                          required
                          value={formData.experience}
                          onChange={(e) => setFormData({...formData, experience: e.target.value})}
                          placeholder="e.g., 5 years"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="businessType">Type of Business *</Label>
                        <Input
                          id="businessType"
                          required
                          value={formData.businessType}
                          onChange={(e) => setFormData({...formData, businessType: e.target.value})}
                          placeholder="e.g., Wholesale, Retail, Distributor"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Why do you want to become a dealer? *</Label>
                      <Textarea
                        id="message"
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        placeholder="Tell us about your business goals and why you want to partner with us..."
                        rows={4}
                      />
                    </div>
                  </div>

                  <Button type="submit" size="lg" className="w-full bg-[#2E3A6F] hover:bg-[#1f2747]">
                    <Handshake className="mr-2" size={20} />
                    Submit Application
                  </Button>

                  <p className="text-sm text-gray-600 text-center">
                    By submitting this form, you agree to our terms and conditions for dealership partnerships.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dealership;
